import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import blogService from '../../../../services/blogService';
import type { IBlog } from '../../../../types/blog';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';

const ManageBlogs: React.FC = () => {
  const [blogs, setBlogs] = useState<IBlog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchBlogs = async () => {
    setIsLoading(true);
    try {
      // Fetch all blogs (published and unpublished) for the admin view
      const data = await blogService.getAllBlogsForAdmin();
      setBlogs(data);
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleDeleteBlog = async (blog: IBlog) => {
    if (!window.confirm(`Are you sure you want to delete the post "${blog.title}"?`)) return;
    setIsProcessing(blog._id);
    try {
      const message = await blogService.deleteBlog(blog._id);
      showToast(message, { type: 'success' });
      fetchBlogs(); // Refetch the list from the server
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handleNavigateToEdit = (blogId: string) => {
    // Navigate to the existing CreateBlog component, but with an ID for editing
    navigate(`/dashboard/admin/create-blog/${blogId}`);
  };

  const handleNavigateToCreate = () => {
    navigate('/dashboard/admin/create-blog');
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="py-10 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Manage Blog Posts</h1>
          <button
            onClick={handleNavigateToCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Post
          </button>
        </div>

        {blogs.length === 0 ? <p>No blog posts found.</p> : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Author</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {blogs.map(blog => (
                  <tr key={blog._id}>
                    <td className="px-6 py-4 font-medium">{blog.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{blog.authorName}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 inline-flex text-xs font-semibold rounded-full ${blog.published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {blog.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium space-x-4">
                      <Link to={`/blogs/${blog.slug}`} target="_blank" className="text-gray-600 hover:text-gray-900 inline-flex items-center">
                        <Eye className="h-5 w-5 mr-1" /> View
                      </Link>
                      <button onClick={() => handleNavigateToEdit(blog._id)} disabled={!!isProcessing} className="text-indigo-600 hover:text-indigo-900">
                        <Edit className="h-5 w-5 mr-1 inline" /> Edit
                      </button>
                      <button onClick={() => handleDeleteBlog(blog)} disabled={isProcessing === blog._id} className="text-red-600 hover:text-red-900">
                        <Trash2 className="h-5 w-5 mr-1 inline" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBlogs;